import React, {useEffect, useState} from "react";
import {Link} from "react-router-dom";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import toast from "react-hot-toast";
import usePermissions from "../../hooks/usePermissions";
import {apiErrorMessage, updateAttendanceSettings} from "../Attendance/attendanceApi";
import useAttendanceSettings, {ATTENDANCE_SETTINGS_KEY} from "../Attendance/useAttendanceSettings";
import {SettingsForm, FormSection, FormField, SwitchRow, SaveAction, ListLoader, inputClass} from "./SettingsUI";

// Réglages > Équipe > Pointage: turns the Pointage module on for this account
// (menu entry, page, badge field on employees) and sets the work-day start.
const AttendanceSettings = () => {
    const {canUpdate} = usePermissions();
    const canManage = canUpdate("settings");
    const queryClient = useQueryClient();
    const {settings, isLoading, isError} = useAttendanceSettings({fresh: true});
    const [form, setForm] = useState(null);

    // Re-synced from every API response (mount refetch + save).
    useEffect(() => {
        if (settings && !isLoading) setForm({enabled: settings.enabled, dayStart: settings.day_start});
    }, [settings, isLoading]);

    const mutation = useMutation({
        mutationFn: () => updateAttendanceSettings({enabled: form.enabled, day_start: form.dayStart}),
        onSuccess: () => {
            // Re-read from the API rather than trusting the PUT echo, so the
            // screen shows what was actually stored.
            queryClient.invalidateQueries({queryKey: ATTENDANCE_SETTINGS_KEY});
            toast.success("Réglages du pointage enregistrés");
        },
        onError: (error) => toast.error(apiErrorMessage(error, "Impossible d'enregistrer les réglages")),
    });

    if (isError && !settings) {
        return <p className="text-sm text-red-500 p-6">Impossible de charger les réglages du pointage.</p>;
    }
    if (isLoading || !form) {
        return <ListLoader label="Chargement des réglages…"/>;
    }

    return (
        <SettingsForm>
            {canManage && <SaveAction onClick={() => mutation.mutate()} saving={mutation.isPending}/>}

            <FormSection>
                <SwitchRow
                    id="attendanceEnabled"
                    label="Activer le pointage"
                    description="Pointeuse empreinte / visage (Hikvision). Ajoute la page « Pointage » au menu et le n° de badge sur la fiche des employés."
                    checked={form.enabled}
                    disabled={!canManage}
                    onChange={(checked) => setForm((prev) => ({...prev, enabled: checked}))}
                />
            </FormSection>

            {form.enabled && (
                <FormSection>
                    <div className="sm:w-64">
                        <FormField id="attendanceDayStart" label="Début de la journée de travail"
                                   hint="Les pointages avant cette heure comptent pour la veille.">
                            <input
                                id="attendanceDayStart"
                                type="time"
                                value={form.dayStart}
                                disabled={!canManage}
                                onChange={(e) => e.target.value && setForm((prev) => ({...prev, dayStart: e.target.value}))}
                                className={inputClass(false)}
                            />
                        </FormField>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                        Ex. 05:00 : une sortie à 01:30 clôture la journée de la veille.
                    </p>
                </FormSection>
            )}

            {settings?.enabled && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                    Présence, pointages et pointeuses : <Link to="/pointage" className="text-primary font-medium hover:underline">ouvrir la page Pointage</Link>.
                </p>
            )}
        </SettingsForm>
    );
};

export default AttendanceSettings;
